import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import ProductCombobox from "@/components/ProductCombobox";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Building2, Printer, Trash2, ChevronRight, ChevronDown, X as XIcon } from "lucide-react";

const emptyItem = () => ({ product_id: "", quantidade: 0, valor_unitario: 0 });
const emptyBatch = { supplier_id: "", nota_fiscal: "", forma_pagamento: "", observacoes: "", itens: [emptyItem()] };

const PAGAMENTO_OPTS = ["À vista", "Boleto", "PIX", "30 dias", "60 dias", "Cartão", "Faturado"];

export default function SupplierPurchases() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [groups, setGroups] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [batch, setBatch] = useState(emptyBatch);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const [g, s, p] = await Promise.all([
      api.get("/supplier-purchases/grouped"),
      api.get("/suppliers"),
      api.get("/products"),
    ]);
    setGroups(g.data); setSuppliers(s.data); setProducts(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setBatch((b) => ({ ...b, itens: [...b.itens, emptyItem()] }));
  const setItem = (i, k, v) => setBatch((b) => { const cp = [...b.itens]; cp[i] = { ...cp[i], [k]: v }; return { ...b, itens: cp }; });
  const removeItem = (i) => setBatch((b) => ({ ...b, itens: b.itens.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const cleanItens = batch.itens
        .filter((it) => it.product_id && parseFloat(it.quantidade) > 0)
        .map((it) => ({ product_id: it.product_id, quantidade: parseFloat(it.quantidade), valor_unitario: parseFloat(it.valor_unitario) || 0 }));
      if (cleanItens.length === 0) { toast.error("Adicione ao menos um item válido"); return; }
      const { data } = await api.post("/supplier-purchases/batch", {
        supplier_id: batch.supplier_id,
        nota_fiscal: batch.nota_fiscal,
        forma_pagamento: batch.forma_pagamento,
        observacoes: batch.observacoes,
        itens: cleanItens,
      });
      toast.success(`Compra ${data.numero} registrada · ${data.quantidade_itens} item(ns) · ${fmtBRL(data.total)}`);
      setOpen(false); setBatch(emptyBatch); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const startNew = () => { setBatch(emptyBatch); setOpen(true); };

  const removeGroup = async (g) => {
    if (g.is_batch) {
      if (!confirm(`Excluir compra ${g.numero} com ${g.itens.length} item(ns) — ${fmtBRL(g.valor_total)}?\n\nATENÇÃO: o estoque de TODOS os itens será estornado.`)) return;
      try {
        await api.delete(`/supplier-purchases/batch/${g.batch_id}`);
        toast.success("Compra excluída · estoque estornado");
        load();
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    } else {
      const one = g.itens[0];
      if (!confirm(`Excluir compra ${g.numero}?\n\nATENÇÃO: o estoque do produto ${one.product_nome} será ESTORNADO (${one.quantidade} un.).`)) return;
      try {
        await api.delete(`/supplier-purchases/${one.id}`);
        toast.success("Compra excluída · estoque estornado");
        load();
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    }
  };

  const batchTotal = batch.itens.reduce((s, it) => s + (parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0), 0);

  const filteredGroups = groups.filter((g) => {
    if (!q) return true;
    const nq = normalize(q);
    if (normalize(g.numero).includes(nq)) return true;
    if (normalize(g.supplier_nome).includes(nq)) return true;
    if (normalize(g.supplier_cnpj).includes(nq)) return true;
    if (normalize(g.nota_fiscal).includes(nq)) return true;
    if (normalize(g.forma_pagamento).includes(nq)) return true;
    return (g.itens || []).some((it) => normalize(it.product_nome).includes(nq) || normalize(it.product_codigo).includes(nq));
  });

  const totalGeral = filteredGroups.reduce((s, g) => s + (g.valor_total || 0), 0);

  return (
    <div data-testid="sup-purchases-page">
      <SectionTitle action={<Btn onClick={startNew} data-testid="add-sup-purchase-btn"><Plus className="w-4 h-4" />Nova Compra</Btn>}>
        Compras de Fornecedores · <Building2 className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar por Nº, fornecedor, CNPJ, NF ou produto..." testid="sup-search" />
        <div className="basis-full md:basis-auto md:ml-auto px-3 py-2 bg-[#3D6B57] text-white font-mono-data text-[11px] uppercase tracking-widest whitespace-nowrap flex items-center gap-2 border-2 border-[#111]">
          <span>{filteredGroups.length} {filteredGroups.length === 1 ? "compra" : "compras"}</span>
          <span className="opacity-50">·</span>
          <b className="text-base">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(totalGeral)}</b>
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Nº</th>
              <th>Data</th>
              <th>Fornecedor</th>
              <th className="text-right">Itens</th>
              <th className="text-right">Total</th>
              <th>NF</th>
              <th>Pgto</th>
              <th className="w-24">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filteredGroups.length === 0 && <EmptyState message={q ? "Nenhum resultado para a busca." : "Nenhuma compra registrada."} cols={9} />}
            {filteredGroups.map((g) => {
              const key = g.batch_id || `single-${g.itens[0]?.id}`;
              const isOpen = !!expanded[key];
              return (
                <React.Fragment key={key}>
                  <tr data-testid={`sup-purchase-row-${g.batch_id || g.itens[0]?.id}`} className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}>
                    <td className="text-center">{g.itens.length > 1 ? (isOpen ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />) : ""}</td>
                    <td className="font-mono-data font-bold">{g.numero}</td>
                    <td className="font-mono-data text-xs">{fmtDate(g.data_compra)}</td>
                    <td>
                      <div className="font-medium">{g.supplier_nome}</div>
                      {g.supplier_cnpj && <div className="font-mono-data text-xs text-[#777]">{g.supplier_cnpj}</div>}
                    </td>
                    <td className="text-right">
                      <Badge variant={g.is_batch ? "green" : "outline"}>{g.itens.length} item{g.itens.length > 1 ? "s" : ""}</Badge>
                    </td>
                    <td className="text-right font-mono-data font-bold">{fmtBRL(g.valor_total)}</td>
                    <td className="font-mono-data text-xs">{g.nota_fiscal || "—"}</td>
                    <td>{g.forma_pagamento ? <Badge variant="outline">{g.forma_pagamento}</Badge> : "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {g.is_batch ? (
                          <a href={`/imprimir/compra-fornecedor-lote/${g.batch_id}`} target="_blank" rel="noreferrer" data-testid={`print-sup-batch-${g.batch_id}`} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir recibo em lote"><Printer className="w-3.5 h-3.5" /></a>
                        ) : (
                          <a href={`/imprimir/compra-fornecedor/${g.itens[0].id}`} target="_blank" rel="noreferrer" data-testid={`print-sup-${g.itens[0].id}`} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir recibo"><Printer className="w-3.5 h-3.5" /></a>
                        )}
                        {canEdit && <button onClick={() => removeGroup(g)} data-testid={`delete-sup-purchase-${g.batch_id || g.itens[0].id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]" title="Excluir · estorna estoque"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                  {isOpen && g.itens.length > 0 && (
                    <tr className="bg-[#FAFAFA]" data-testid={`sup-items-detail-${g.batch_id || g.itens[0]?.id}`}>
                      <td colSpan={9} className="p-0">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-[#F0F7F2]">
                              <th className="text-left px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Código</th>
                              <th className="text-left px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Produto</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Qtd</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Vlr Un</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.itens.map((it) => (
                              <tr key={it.id} data-testid={`sup-item-${it.id}`}>
                                <td className="px-3 py-1 font-mono-data text-xs">{it.product_codigo}</td>
                                <td className="px-3 py-1">{it.product_nome}</td>
                                <td className="px-3 py-1 text-right font-mono-data">{it.quantidade}</td>
                                <td className="px-3 py-1 text-right font-mono-data">{fmtBRL(it.valor_unitario)}</td>
                                <td className="px-3 py-1 text-right font-mono-data font-bold">{fmtBRL(it.valor_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nova Compra de Fornecedor" size="xl">
        <form onSubmit={submit} className="space-y-4" data-testid="sup-purchase-form">
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Fornecedor *" required value={batch.supplier_id} onChange={(v) => setBatch({ ...batch, supplier_id: v })}
              options={suppliers.map((s) => ({ value: s.id, label: `${s.nome}${s.cnpj ? " · " + s.cnpj : (s.cpf ? " · " + s.cpf : "")}` }))} colspan />
            <Field label="Nota Fiscal" value={batch.nota_fiscal} onChange={(v) => setBatch({ ...batch, nota_fiscal: v })} />
            <SelectField label="Forma de Pagamento" value={batch.forma_pagamento} onChange={(v) => setBatch({ ...batch, forma_pagamento: v })}
              options={PAGAMENTO_OPTS.map((o) => ({ value: o, label: o }))} />
            <TextArea label="Observações (aplicadas a todos os itens)" value={batch.observacoes} onChange={(v) => setBatch({ ...batch, observacoes: v })} colspan />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Itens da compra *</label>
              <button type="button" onClick={addItem} data-testid="add-sup-item-btn" className="text-xs font-mono-data uppercase tracking-widest text-[#FF4F00] hover:underline">+ Adicionar item</button>
            </div>
            <div className="space-y-2" data-testid="sup-items-list">
              {batch.itens.map((it, i) => (
                <div key={i} className="brutal-card-sm p-2 grid grid-cols-12 gap-2 items-center" data-testid={`sup-item-row-${i}`}>
                  <div className="col-span-6">
                    <ProductCombobox
                      items={products}
                      value={it.product_id}
                      onChange={(id, prod) => {
                        setItem(i, "product_id", id);
                        if (prod && prod.valor_medio && (!it.valor_unitario || parseFloat(it.valor_unitario) === 0)) {
                          setItem(i, "valor_unitario", Number(prod.valor_medio).toFixed(2));
                        }
                      }}
                      testid={`sup-item-product-${i}`}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0.01" required value={it.quantidade} onChange={(e) => setItem(i, "quantidade", e.target.value)} placeholder="Qtd" className="w-full border-2 border-[#111] p-2 text-sm text-right font-mono-data" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" step="0.01" min="0" required value={it.valor_unitario} onChange={(e) => setItem(i, "valor_unitario", e.target.value)} placeholder="Vlr un." className="w-full border-2 border-[#111] p-2 text-sm text-right font-mono-data" />
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <span className="font-mono-data text-xs font-bold whitespace-nowrap text-[#3D6B57]">{fmtBRL((parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0))}</span>
                    {batch.itens.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} data-testid={`remove-sup-item-${i}`} className="p-1 border-2 border-[#111] hover:bg-[#E60000] hover:text-white">
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#FAFAFA] border-2 border-[#111] p-3 flex justify-between items-center">
            <span className="font-mono-data text-xs text-[#555]">Total da compra ({batch.itens.length} item{batch.itens.length > 1 ? "s" : ""})</span>
            <span className="font-display font-black text-2xl text-[#3D6B57]" data-testid="batch-total">{fmtBRL(batchTotal)}</span>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-sup-purchase-btn">Registrar Compra</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
